// Catalog.ID Client — Public Claims API
// ============================================================================

import { api, ApiError } from './client';
import type {
    PublicClaimSummary,
    PublishClaimRequest,
    ReviewPublicClaimRequest,
    OperatorInfo,
    PendingPublicClaim,
} from './types';

export async function publishClaim(claimUuid: string, req: PublishClaimRequest): Promise<void> {
    return api.post(`/api/claims/${claimUuid}/publish`, req);
}

export async function listPublicClaims(): Promise<PublicClaimSummary[]> {
    return api.get('/api/publicclaims');
}

export async function listPendingPublicClaims(): Promise<PendingPublicClaim[]> {
    return api.get('/api/publicclaims/pending');
}

export async function deletePublicClaim(id: number): Promise<void> {
    return api.del(`/api/publicclaims/${id}`);
}

export async function reviewPublicClaim(id: number, req: ReviewPublicClaimRequest): Promise<void> {
    return api.put(`/api/publicclaims/${id}/review`, req);
}

export async function listOperators(): Promise<OperatorInfo[]> {
    return api.get('/api/operators');
}

/**
 * Message for a 403 from an operator-gated endpoint, or null when the error is
 * something else (so the caller keeps its own handling — `origin_only` and the
 * frozen-org refusal both come back as 403 too).
 *
 * Worth its own message because the derivation design created a failure mode
 * the account flag did not have: operator powers now come from an active CPR
 * operator↔org binding plus an owner/admin org role (operator-org-binding plan
 * §2.7), and BOTH can change while a session is open — a binding release or a
 * role demotion revokes them immediately. The client only re-reads its
 * `isOperator` snapshot at login, so the operator workspace stays visible and
 * the next call simply fails. A bare "Operator access required" would read as a
 * bug; this says what actually happened and what to do.
 */
export function operatorAccessRevokedMessage(err: unknown): string | null {
    if (!(err instanceof ApiError) || err.status !== 403) return null;
    const body = err.body as Record<string, unknown> | null;
    if (!body || body.error !== 'Operator access required') return null;
    return 'This session no longer has operator access. Operator powers come from '
        + "your organization's operator binding and your role in it — either the "
        + 'binding was released or your role changed. Sign in again to refresh.';
}
